import React from 'react';
import DOMPurify from 'dompurify';

/**
 * Safely sanitizes HTML content to prevent XSS attacks
 * @param dirty - The unsanitized HTML string
 * @returns Sanitized HTML string safe for rendering
 */
export const sanitizeHtml = (dirty: string): string => {
  if (typeof window === 'undefined') {
    // Server-side rendering fallback
    return dirty.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  }

  return DOMPurify.sanitize(dirty);
};

/**
 * React component for safely rendering HTML content
 * Usage: <SafeHtml content={htmlString} />
 */
export interface SafeHtmlProps {
  content: string;
  className?: string;
  tag?: keyof JSX.IntrinsicElements;
}

export const SafeHtml: React.FC<SafeHtmlProps> = ({ 
  content, 
  className = '', 
  tag = 'div'
}) => {
  const Tag = tag;
  const sanitizedContent = sanitizeHtml(content);
  
  return React.createElement(Tag, {
    className,
    dangerouslySetInnerHTML: { __html: sanitizedContent }
  });
};

/**
 * For cases where only basic text with simple formatting is needed
 * Strips all HTML tags except basic formatting
 */
export const sanitizeBasicHtml = (dirty: string): string => {
  if (typeof window === 'undefined') {
    return dirty.replace(/<(?!\/?(strong|em|u|i|b|span|br)\b)[^>]*>/gi, '');
  }
  
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['strong', 'em', 'u', 'i', 'b', 'span', 'br'],
    ALLOWED_ATTR: ['class']
  });
};

/**
 * For code blocks - preserves code formatting but prevents XSS
 */
export const sanitizeCodeHtml = (dirty: string): string => {
  if (typeof window === 'undefined') {
    return dirty.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  }
  
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['code', 'pre', 'span', 'br', 'div'],
    ALLOWED_ATTR: ['class', 'style']
  });
}; 